import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@/lib/api/client';
import { jobSectionPath } from '@/lib/utils/navPaths';
import type { JobTaskCard } from '@/lib/api/dashboard';

interface JobTaskListsProps {
  tasks: JobTaskCard[];
  onChanged: () => void;
}

const statusDot: Record<JobTaskCard['status'], string> = {
  pending: 'bg-gray-300',
  in_progress: 'bg-amber-400',
  completed: 'bg-success',
  blocked: 'bg-critical',
};

interface ChecklistItem { id: number; text: string; checked: number | boolean }

// Per-job expanded task lists on the dashboard (CR #87): every active job's
// planner cards, grouped and expanded, with tap-to-complete.
export const JobTaskLists = ({ tasks, onChanged }: JobTaskListsProps) => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [busyId, setBusyId] = useState<number | null>(null);
  // #93 — expandable sub-task checklists under each task row
  const [openTasks, setOpenTasks] = useState<Set<number>>(new Set());
  const [checklists, setChecklists] = useState<Record<number, ChecklistItem[]>>({});

  const toggleChecklist = async (taskId: number) => {
    setOpenTasks(prev => {
      const n = new Set(prev);
      if (n.has(taskId)) n.delete(taskId); else n.add(taskId);
      return n;
    });
    if (!checklists[taskId]) {
      try {
        const r = await apiClient.get(`/planner/tasks/${taskId}/checklist`);
        setChecklists(prev => ({ ...prev, [taskId]: r.data.data ?? [] }));
      } catch {
        setChecklists(prev => ({ ...prev, [taskId]: [] }));
      }
    }
  };

  const toggleChecklistItem = async (taskId: number, item: ChecklistItem) => {
    const next = !item.checked;
    setChecklists(prev => ({
      ...prev,
      [taskId]: (prev[taskId] || []).map(c => (c.id === item.id ? { ...c, checked: next } : c)),
    }));
    try {
      await apiClient.patch(`/planner/checklist/${item.id}`, { checked: next });
      onChanged(); // refresh the x/y counts
    } catch {
      setChecklists(prev => ({
        ...prev,
        [taskId]: (prev[taskId] || []).map(c => (c.id === item.id ? { ...c, checked: !next } : c)),
      }));
    }
  };

  const groups = useMemo(() => {
    const byJob = new Map<number, { job_id: number; job_title: string; job_number: string; client_id: number | null; tasks: JobTaskCard[] }>();
    for (const t of tasks) {
      if (!byJob.has(t.job_id)) {
        byJob.set(t.job_id, { job_id: t.job_id, job_title: t.job_title, job_number: t.job_number, client_id: t.client_id, tasks: [] });
      }
      byJob.get(t.job_id)!.tasks.push(t);
    }
    return [...byJob.values()];
  }, [tasks]);

  if (groups.length === 0) return null;

  const toggleJob = (jobId: number) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });

  const toggleComplete = async (t: JobTaskCard) => {
    setBusyId(t.id);
    try {
      await apiClient.patch(`/planner/tasks/${t.id}`, {
        status: t.status === 'completed' ? 'pending' : 'completed',
      });
      onChanged();
    } catch { /* next poll restores truth */ }
    finally { setBusyId(null); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold leading-tight tracking-[-0.015em] text-gray-900">
          Job Task Lists
        </h2>
        <Link to="/planner" className="text-sm font-medium text-primary hover:underline">Open planner</Link>
      </div>
      <div className="flex flex-col gap-3">
        {groups.map(g => {
          const done = g.tasks.filter(t => t.status === 'completed').length;
          const open = !collapsed.has(g.job_id);
          return (
            <div key={g.job_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleJob(g.job_id)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                <span className={`material-symbols-outlined text-lg text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>chevron_right</span>
                <span className="font-bold text-gray-900 text-sm truncate flex-1">
                  <span className="text-primary text-xs font-bold mr-1.5">{g.job_number}</span>
                  {g.job_title}
                </span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  done === g.tasks.length ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'
                }`}>
                  {done}/{g.tasks.length} done
                </span>
              </button>
              {open && (
                <ul className="border-t border-gray-100 divide-y divide-gray-50">
                  {g.tasks.map(t => (
                    <li key={t.id}>
                      <div className="flex items-center gap-2.5 px-4 py-2">
                        <button
                          onClick={() => toggleComplete(t)}
                          disabled={busyId === t.id}
                          title={t.status === 'completed' ? 'Mark not done' : 'Mark done'}
                          className={`h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            t.status === 'completed'
                              ? 'bg-success border-success text-white'
                              : 'border-gray-300 hover:border-success text-transparent'
                          } ${busyId === t.id ? 'opacity-50' : ''}`}
                        >
                          <span className="material-symbols-outlined text-[14px] leading-none">check</span>
                        </button>
                        <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${statusDot[t.status]}`} title={t.status.replace('_', ' ')} />
                        <span className={`text-sm flex-1 truncate ${t.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                          {t.title}
                        </span>
                        {t.checklist_total > 0 && (
                          <button
                            onClick={() => toggleChecklist(t.id)}
                            title={openTasks.has(t.id) ? 'Hide sub-tasks' : 'Show sub-tasks'}
                            className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 flex-shrink-0 flex items-center gap-0.5 transition-colors ${
                              openTasks.has(t.id) ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400 hover:bg-primary/10 hover:text-primary'
                            }`}
                          >
                            {t.checklist_done}/{t.checklist_total}
                            <span className={`material-symbols-outlined text-[12px] leading-none transition-transform ${openTasks.has(t.id) ? 'rotate-180' : ''}`}>expand_more</span>
                          </button>
                        )}
                        {t.start_date && (
                          <span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0 hidden sm:inline">
                            {new Date(t.start_date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {t.assignee_name && (
                          <span className="text-[11px] text-gray-500 truncate max-w-[7rem] flex-shrink-0 hidden md:inline">{t.assignee_name}</span>
                        )}
                      </div>
                      {/* Sub-task checklist (#93) */}
                      {openTasks.has(t.id) && (
                        <ul className="pb-2 pl-12 pr-4 space-y-0.5">
                          {!checklists[t.id] ? (
                            <li className="text-[11px] text-gray-300 py-0.5">Loading…</li>
                          ) : checklists[t.id].length === 0 ? (
                            <li className="text-[11px] text-gray-400 italic py-0.5">No sub-tasks on this card.</li>
                          ) : checklists[t.id].map(item => (
                            <li key={item.id}>
                              <label className="flex items-center gap-2 py-0.5 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={!!item.checked}
                                  onChange={() => toggleChecklistItem(t.id, item)}
                                  className="accent-[#C77D3F] h-3.5 w-3.5"
                                />
                                <span className={`text-xs ${item.checked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                  {item.text}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {open && (
                <div className="border-t border-gray-100 px-4 py-2">
                  <Link
                    to={jobSectionPath(g.client_id ?? undefined, g.job_id, 'tasks')}
                    className="text-xs text-primary font-medium hover:underline"
                  >
                    Open this job's tasks →
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
